import { Box, Card, Skeleton } from 'components/base';
import { GoogleMap } from 'components/atoms';
import { useGooglePlaces } from 'hooks/common';
import useCustomForm from 'pages/Forms/context/useCustomForm';
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getCurrentPosition } from 'utils/map';
import { Logger } from 'utils/logger';

const defaultMapOptions = {
  fullscreenControl: true,
  streetViewControl: false,
  mapTypeControl: false,
};

const zoomLevel = 15;

interface ICurrentLocation {
  lat: number;
  long: number;
  address: string;
}

type IUseCustomForm = [
  {
    currentLocation: ICurrentLocation;
  },
  {
    setCurrentLocation: Dispatch<SetStateAction<ICurrentLocation>>;
  },
];

interface IGeoLocationProps {
  editedGeoData?: ICurrentLocation;
}

export const GeoLocation: FC<IGeoLocationProps> = ({ editedGeoData }) => {
  const { getGeocodeByLatLng } = useGooglePlaces();
  const [{ currentLocation }, { setCurrentLocation }] =
    useCustomForm() as unknown as IUseCustomForm;
  const [isLoading, setIsLoading] = useState(false);

  const formattedLocation = useMemo(
    () => ({
      lat: currentLocation?.lat,
      lng: currentLocation?.long,
    }),
    [currentLocation],
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get current position
        const position = await getCurrentPosition();
        const { latitude, longitude } = position.coords;

        const newAddress = await getGeocodeByLatLng(latitude, longitude);

        if (editedGeoData) {
          setCurrentLocation(editedGeoData);
        } else {
          setCurrentLocation({
            lat: latitude,
            long: longitude,
            address: newAddress?.[0]?.formatted_address ?? '',
          });
        }
      } catch (error) {
        Logger.error({ message: 'Error getting current location', error });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedGeoData]);

  if (isLoading) {
    return (
      <Box height={250} width={800} p={1}>
        <Card sx={{ height: '100%', width: '100%' }}>
          <Skeleton variant="rounded" width="100%" height="100%" />
        </Card>
      </Box>
    );
  }

  return (
    currentLocation && (
      <Box height={250} width={800} p={1}>
        <Card sx={{ height: '100%', width: '100%' }}>
          <GoogleMap
            location={formattedLocation}
            height="100%"
            width="100%"
            draggable={false}
            canClickMap={false}
            defaultOptions={defaultMapOptions}
            zoom={zoomLevel}
          />
        </Card>
      </Box>
    )
  );
};
